import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Users as UsersIcon,
  Search,
  RefreshCw,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Download,
  Gift,
  Ban,
  Check,
  Filter,
  X,
  Mail,
  MessageCircle,
  Smartphone
} from 'lucide-react';
import './Users.css';

/** Локальный (анонимный) аккаунт устройства — id вида device_<uuid> */
const isDeviceAccount = (user) => String(user?.id || '').startsWith('device_');

/**
 * Отображаемое имя.
 * У device-аккаунтов имя в БД — служебное ("your iPhone", сам id или пусто),
 * поэтому показываем понятную подпись. Если к устройству уже привязан
 * Apple/Google — показываем реальное имя пользователя.
 */
const getDisplayName = (user) => {
  const real = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    || (user?.username || '').trim();

  if (isDeviceAccount(user)) {
    const looksTechnical =
      !real ||
      real.toLowerCase().startsWith('your') ||
      real.toLowerCase().startsWith('iphone') ||
      real.startsWith('device_') ||
      real === '0';
    if (looksTechnical) return 'Локальный аккаунт устройства';
    return real;
  }
  return real || user?.email || user?.id || '—';
};

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paidDaysFrom, setPaidDaysFrom] = useState('');
  const [paidDaysTo, setPaidDaysTo] = useState('');
  const [authProvider, setAuthProvider] = useState('');
  
  // Selection
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Gift modal
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftUserId, setGiftUserId] = useState(null);
  const [giftMonths, setGiftMonths] = useState(1);
  const [giftLoading, setGiftLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [page]);

  // Быстрое переключение категории (табы) — сразу перезагружаем список
  const selectCategory = (value) => {
    setAuthProvider(value);
    setPage(1);
    loadUsers({ authProviderOverride: value, pageOverride: 1 });
  };

  const loadUsers = async (opts = {}) => {
    setLoading(true);
    try {
      const provider = opts.authProviderOverride !== undefined ? opts.authProviderOverride : authProvider;
      const params = { page: opts.pageOverride || page, limit: 50 };
      if (search) params.search = search;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (paidDaysFrom) params.paidDaysFrom = paidDaysFrom;
      if (paidDaysTo) params.paidDaysTo = paidDaysTo;
      if (provider) params.authProvider = provider;
      
      const response = await api.get('/api/admin/users', { params });
      if (response.data.success) {
        setUsers(response.data.users || []);
        setTotalPages(response.data.totalPages || 1);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetUser = async (user) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || user.id;
    if (!window.confirm(`Сбросить пользователя "${name}"?\n\nБудут удалены: профиль, подписка, просмотры.\nКомментарии останутся.`)) return;
    try {
      const response = await api.delete(`/api/admin/users/${user.id}/reset`);
      if (response.data.success) {
        loadUsers();
      }
    } catch (error) {
      console.error('Ошибка сброса пользователя:', error);
      alert('Ошибка при сбросе пользователя');
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadUsers();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPaidDaysFrom('');
    setPaidDaysTo('');
    setAuthProvider('');
    setPage(1);
    setTimeout(loadUsers, 0);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.id)));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
    setSelectAll(newSelected.size === users.length);
  };

  // Export to CSV
  const exportUsers = async () => {
    try {
      const params = {};
      if (selectedUsers.size > 0) {
        params.selectedIds = Array.from(selectedUsers).join(',');
      } else {
        if (search) params.search = search;
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
      }
      
      const response = await api.get('/api/admin/users/export', { 
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (error) {
      console.error('Ошибка экспорта:', error);
      alert('Ошибка экспорта пользователей');
    }
  };

  // Gift subscription
  const openGiftModal = (userId = null) => {
    setGiftUserId(userId);
    setGiftMonths(1);
    setShowGiftModal(true);
  };

  const quickGift = async (userId, months) => {
    if (!window.confirm(`Подарить ${months} мес. подписки?`)) return;
    try {
      await api.post(`/api/admin/users/${userId}/gift`, { months });
      alert('Подарок успешно вручён!');
      loadUsers();
    } catch (error) {
      console.error('Ошибка подарка:', error);
      alert('Ошибка: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleGift = async () => {
    setGiftLoading(true);
    try {
      if (giftUserId) {
        await api.post(`/api/admin/users/${giftUserId}/gift`, { months: giftMonths });
        alert(`Подарок успешно вручён!`);
      } else {
        const userIds = Array.from(selectedUsers);
        await api.post('/api/admin/users/bulk-gift', { userIds, months: giftMonths });
        alert(`Подарок вручён ${userIds.length} пользователям!`);
      }
      setShowGiftModal(false);
      loadUsers();
    } catch (error) {
      console.error('Ошибка подарка:', error);
      alert('Ошибка: ' + (error.response?.data?.error || error.message));
    } finally {
      setGiftLoading(false);
    }
  };

  // Block/unblock user
  const toggleBlock = async (user) => {
    const action = user.is_blocked ? 'разблокировать' : 'заблокировать';
    if (!window.confirm(`Вы уверены, что хотите ${action} пользователя ${getDisplayName(user)}?`)) {
      return;
    }
    
    try {
      await api.post(`/api/admin/users/${user.id}/block`, { 
        blocked: !user.is_blocked,
        reason: user.is_blocked ? null : 'Заблокирован администратором'
      });
      loadUsers();
    } catch (error) {
      console.error('Ошибка блокировки:', error);
      alert('Ошибка: ' + (error.response?.data?.error || error.message));
    }
  };

  // Sync users from miniapp JSON to SQLite
  const syncUsers = async () => {
    try {
      const res = await api.post('/api/admin/users/sync');
      if (res.data.success) {
        alert(`Синхронизировано: ${res.data.synced} пользователей`);
        loadUsers();
      } else {
        alert('Ошибка: ' + res.data.error);
      }
    } catch (error) {
      console.error('Sync error:', error);
      alert('Ошибка синхронизации: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="users-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Пользователи</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={syncUsers} title="Синхронизировать с miniapp">
            <RefreshCw size={18} />
            Синхр.
          </button>
          <button className="btn btn-secondary" onClick={exportUsers}>
            <Download size={18} />
            Сохранить
          </button>
          <button className="btn btn-secondary" onClick={loadUsers}>
            <RefreshCw size={18} />
            Обновить
          </button>
        </div>
      </div>

      {/* Категории пользователей */}
      <div className="users-categories">
        {[
          { value: '', label: 'Все' },
          { value: 'account', label: 'Обычные аккаунты' },
          { value: 'device', label: 'Локальные аккаунты устройств' },
          { value: 'device_linked', label: 'Устройства с привязкой' },
        ].map((cat) => (
          <button
            key={cat.value || 'all'}
            className={`btn ${authProvider === cat.value ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => selectCategory(cat.value)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="users-filters">
        <div className="filters-row">
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              className="input"
              placeholder="Поиск по имени, email, telegram..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </div>
          <button className="btn btn-primary" onClick={handleSearch}>
            <Search size={18} />
            Поиск
          </button>
          <button 
            className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            Фильтры
          </button>
          {(search || dateFrom || dateTo || paidDaysFrom || paidDaysTo || authProvider) && (
            <button className="btn btn-ghost" onClick={clearFilters}>
              <X size={18} />
              Сбросить
            </button>
          )}
        </div>
        
        {showFilters && (
          <div className="filters-extended">
            <div className="filter-group">
              <label>Дата регистрации:</label>
              <div className="filter-range">
                <input
                  type="date"
                  className="input"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <span>—</span>
                <input
                  type="date"
                  className="input"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
            <div className="filter-group">
              <label>Оплаченных дней:</label>
              <div className="filter-range">
                <input
                  type="number"
                  className="input"
                  value={paidDaysFrom}
                  onChange={(e) => setPaidDaysFrom(e.target.value)}
                  placeholder="От"
                  min="0"
                />
                <span>—</span>
                <input
                  type="number"
                  className="input"
                  value={paidDaysTo}
                  onChange={(e) => setPaidDaysTo(e.target.value)}
                  placeholder="До"
                  min="0"
                />
              </div>
            </div>
            <div className="filter-group">
              <label>Тип регистрации:</label>
              <select
                className="input"
                value={authProvider}
                onChange={(e) => setAuthProvider(e.target.value)}
              >
                <option value="">Все</option>
                <option value="account">Обычные аккаунты</option>
                <option value="device">Локальные аккаунты устройств</option>
                <option value="device_linked">Устройства с привязкой</option>
                <option value="telegram">Telegram</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="apple">Apple</option>
                <option value="google">Google</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {selectedUsers.size > 0 && (
        <div className="bulk-actions">
          <span>Выбрано: {selectedUsers.size}</span>
          <button className="btn btn-success btn-sm" onClick={() => openGiftModal(null)}>
            <Gift size={16} />
            Подарить выбранным
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="users-stats">
        <span>Всего: {total}</span>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <span>Загрузка...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <UsersIcon size={48} className="empty-state-icon" />
            <h3 className="empty-state-title">Нет пользователей</h3>
            <p className="empty-state-text">Пользователи появятся после регистрации</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table users-table">
              <thead>
                <tr>
                  <th className="th-checkbox">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Пользователь</th>
                  <th>Аккаунты</th>
                  <th>Подписка</th>
                  <th>Дней осталось</th>
                  <th>Регистрация</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className={user.is_blocked ? 'row-blocked' : ''}>
                    <td className="td-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.id)}
                        onChange={() => toggleSelectUser(user.id)}
                      />
                    </td>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">
                          {isDeviceAccount(user)
                            ? <Smartphone size={16} />
                            : (user.firstName?.[0]?.toUpperCase() || user.username?.[0]?.toUpperCase() || '?')}
                        </div>
                        <div className="user-info">
                          <span className="user-name">
                            {getDisplayName(user)}
                            {isDeviceAccount(user) && (
                              <span className="badge badge-secondary ml-2">устройство</span>
                            )}
                            {user.is_blocked && <span className="badge badge-danger ml-2">Заблокирован</span>}
                          </span>
                          {user.email && (
                            <span className="user-email">
                              <Mail size={12} /> {user.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="accounts-cell">
                        {user.telegram_username && (
                          <span className="account-badge tg">
                            <MessageCircle size={12} /> @{user.telegram_username}
                          </span>
                        )}
                        {user.whatsapp_account && (
                          <span className="account-badge wa">WA: {user.whatsapp_account}</span>
                        )}
                        {user.apple_account && (
                          <span className="account-badge apple">Apple</span>
                        )}
                        {user.google_account && (
                          <span className="account-badge google">Google</span>
                        )}
                        {!user.telegram_username && !user.whatsapp_account && !user.apple_account && !user.google_account && (
                          <span className="text-muted">—</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${user.subscription_status === 'active' ? 'badge-success' : 'badge-neutral'}`}>
                        {user.subscription_status === 'active' ? 'Активна' : 'Нет'}
                      </span>
                      {user.giftedMonths > 0 && (
                        <span className="badge badge-info ml-1" title="Подарено">
                          <Gift size={10} /> {user.giftedMonths}м
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={user.paid_days_remaining > 0 ? 'text-success' : 'text-muted'}>
                        {user.paid_days_remaining || 0}
                      </span>
                    </td>
                    <td className="text-muted">
                      <Calendar size={14} style={{ marginRight: 4 }} />
                      {formatDate(user.createdAt)}
                    </td>
                    <td>
                      <div className="actions-cell">
                        <div className="gift-inline">
                          <select 
                            className="input gift-select"
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                quickGift(user.id, parseInt(e.target.value));
                                e.target.value = '';
                              }
                            }}
                          >
                            <option value="">Подарить</option>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                              <option key={m} value={m}>{m} мес.</option>
                            ))}
                          </select>
                        </div>
                        <button 
                          className={`btn btn-sm ${user.is_blocked ? 'btn-success' : 'btn-danger'}`}
                          onClick={() => toggleBlock(user)}
                          title={user.is_blocked ? 'Разблокировать' : 'Заблокировать'}
                        >
                          {user.is_blocked ? <Check size={14} /> : <Ban size={14} />}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => resetUser(user)}
                          title="Сбросить пользователя (комментарии останутся)"
                          style={{ color: '#ef4444' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="pagination-info">
              Страница {page} из {totalPages}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Gift Modal */}
      {showGiftModal && (
        <div className="modal-overlay" onClick={() => setShowGiftModal(false)}>
          <div className="modal gift-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Подарить подписку</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowGiftModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>
                {giftUserId 
                  ? 'Выберите количество месяцев подписки:'
                  : `Подарить подписку ${selectedUsers.size} пользователям:`
                }
              </p>
              <div className="gift-months-select">
                <label>Количество месяцев:</label>
                <select 
                  className="input select"
                  value={giftMonths}
                  onChange={(e) => setGiftMonths(parseInt(e.target.value))}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                    <option key={m} value={m}>{m} мес.</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowGiftModal(false)}>
                Отмена
              </button>
              <button 
                className="btn btn-success" 
                onClick={handleGift}
                disabled={giftLoading}
              >
                {giftLoading ? 'Дарим...' : 'Подарить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
